import {Controller, UseFieldArrayRemove, useFormContext} from "react-hook-form";
import {
  OfferingFormCheckoutFormQuestionField,
  OfferingFormCheckoutFormValues,
} from "../../../types";
import {
  AnimatePresence,
  Reorder,
  useDragControls,
  useMotionValue,
  motion,
} from "framer-motion";
import {OptionList} from "./option-list";
import {
  StyledCheckoutFormQuestionHeaderWrapper,
  StyledCheckoutFormQuestionInputWrapper,
  StyledCheckoutFormQuestionWrapper,
} from "../../../style";
import {Button, Input, Select, Space, Switch} from "antd";
import {DragIcon} from "../../../../../../icons/icons";
import {FormField} from "../../../../../../../design-system/form-field";
import {hasOptions} from "../../../validation-schema";
import {StyledQuestionRequiredContainer} from "../../../../../../../styles/onboarding/onboarding";
import {DeleteOutlined, DownOutlined} from "@ant-design/icons";
import {useOfferingFormCheckoutFormAccordion} from "../../../hooks/use-question-accordion";
import {offeringFormCheckoutFormQuestionAccordionReducer} from "../../../reducers";
import {useRaisedShadow} from "../../../utils/use-raised-shadow";

export type OfferingFormCheckoutFormAddedQuestionItemProps = {
  item: OfferingFormCheckoutFormQuestionField;
  index: number;
  remove: UseFieldArrayRemove;
};

export function OfferingFormCheckoutFormAddedQuestionItem({
  index,
  item,
  remove,
}: OfferingFormCheckoutFormAddedQuestionItemProps) {
  const y = useMotionValue(0);
  const boxShadow = useRaisedShadow(y);
  const dragControls = useDragControls();
  const {openIndexes, toggleIndex} = useOfferingFormCheckoutFormAccordion({
    reducer: offeringFormCheckoutFormQuestionAccordionReducer,
  });
  const {control, formState, clearErrors, watch} =
    useFormContext<OfferingFormCheckoutFormValues>();
  return (
    <Reorder.Item
      value={item}
      id={item.id}
      style={{boxShadow, y}}
      dragListener={false}
      dragControls={dragControls}
    >
      <StyledCheckoutFormQuestionWrapper>
        <Space
          size={openIndexes.includes(index) ? 16 : 0}
          direction="vertical"
          style={{display: "flex"}}
        >
          <div>
            <StyledCheckoutFormQuestionHeaderWrapper>
              <span className="header-text">Question</span>
              <div className="drag-icon">
                <Button
                  type="text"
                  shape="circle"
                  icon={<DragIcon />}
                  size="small"
                  onPointerDown={(event) => dragControls.start(event)}
                />
              </div>
            </StyledCheckoutFormQuestionHeaderWrapper>
            <div>
              <StyledCheckoutFormQuestionInputWrapper>
                <div className="input-wrapper">
                  <FormField
                    id={`checkoutForm-${item.id}`}
                    renderInput={(id) => (
                      <Controller
                        control={control}
                        name={`bookingQuestions.${index}.question`}
                        render={({
                          field: {onChange, value, ref, onBlur},
                          fieldState: {error},
                        }) => (
                          <>
                            <Input
                              id={id}
                              status={error?.message ? "error" : ""}
                              onChange={onChange}
                              onBlur={onBlur}
                              size="large"
                              value={value}
                              placeholder="E.g. What is your goal?"
                              ref={ref}
                            />
                            {error?.message && (
                              <div role="alert" className="ant-form-item-explain-error">
                                {error.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    )}
                  />
                </div>
                <div className="button-wrapper">
                  <Button
                    onClick={() => remove(index)}
                    type="text"
                    shape="circle"
                    icon={<DeleteOutlined />}
                    size="small"
                  />
                  <Button
                    type="text"
                    shape="circle"
                    icon={<DownOutlined rotate={openIndexes.includes(index) ? 0 : 180} />}
                    size="small"
                    onClick={() => toggleIndex(index)}
                  />
                </div>
              </StyledCheckoutFormQuestionInputWrapper>
            </div>
          </div>
          <div>
            <AnimatePresence initial={false}>
              {openIndexes.includes(index) && (
                <motion.div
                  key="content"
                  initial="collapsed"
                  animate="open"
                  exit="collapsed"
                  variants={{
                    open: {opacity: 1, height: "auto"},
                    collapsed: {opacity: 0, height: 0},
                  }}
                  transition={{duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98]}}
                >
                  <Space size={16} style={{display: "flex"}} direction="vertical">
                    <div>
                      <FormField
                        id={`checkoutFormAnswerType-${item.id}`}
                        label={`Answer type`}
                        renderInput={(id) => (
                          <Controller
                            control={control}
                            name={`bookingQuestions.${index}.answerType`}
                            render={({field: {value, onChange}, fieldState: {error}}) => (
                              <>
                                <Select
                                  style={{width: "100%"}}
                                  value={value}
                                  size="large"
                                  placeholder="Please select"
                                  onChange={(e) => {
                                    // console.log({e});
                                    if (!hasOptions.includes(e)) {
                                      clearErrors(`bookingQuestions.${index}.options`);
                                    }
                                    onChange(e);
                                  }}
                                  options={[
                                    {
                                      value: "short-text",
                                      label: "Short text",
                                    },
                                    {
                                      value: "long-text",
                                      label: "Long Text",
                                    },
                                    {
                                      value: "radio-button",
                                      label: "Radio button",
                                    },
                                    {
                                      value: "checkbox",
                                      label: "Checkbox",
                                    },
                                    {
                                      value: "dropdown",
                                      label: "Dropdown",
                                    },
                                    {
                                      value: "link",
                                      label: "Link",
                                    },
                                  ]}
                                />
                                {error?.message && (
                                  <div
                                    role="alert"
                                    className="ant-form-item-explain-error"
                                  >
                                    {error.message}
                                  </div>
                                )}
                              </>
                            )}
                          />
                        )}
                      />
                    </div>
                    {hasOptions.includes(
                      watch(`bookingQuestions.${index}.answerType`)
                    ) && (
                      <OptionList
                        formState={formState}
                        control={control}
                        nestIndex={index}
                        clearErrors={clearErrors}
                      />
                    )}
                    <div>
                      <Controller
                        control={control}
                        name={`bookingQuestions.${index}.required`}
                        render={({field: {value, onChange}}) => (
                          <StyledQuestionRequiredContainer style={{marginTop: 0}}>
                            <Switch onChange={onChange} checked={value} />{" "}
                            <span className="required-text">
                              Set question as required
                            </span>
                          </StyledQuestionRequiredContainer>
                        )}
                      />
                    </div>
                  </Space>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Space>
      </StyledCheckoutFormQuestionWrapper>
      {/* <ReorderIcon dragControls={dragControls} /> */}
    </Reorder.Item>
  );
}
